import { Alert } from 'components/Alert';
import AbsoluteSpinner from 'components/common/AbsoluteSpinner/AbsoluteSpinner';
import { LazyMuiAvatar } from 'components/LazyAvatar';
import Avatar from 'components/UI/Avatar/Avatar';
import DataLoader from 'components/UI/DataLoader/DataLoader';
import Svg from 'components/UI/Svg/Svg';
import { Color } from 'modules/styles/colors';
import { FontWeight, IconSize } from 'modules/styles/variables';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import { ID_TYPE_CORPPROV } from 'store/findProvider/constants';
import { updatePreferredDoctorsAndRefreshMyDoctors } from 'store/myDoctors/actions';
import { ProviderDetailCriteria, ProviderSummary } from 'store/findProvider/types';
import {
  currentConsumerPcpDoctorsSelector,
  currentConsumerPreferredDoctorsSelector
} from 'store/myDoctors/selectors';
import { RootDispatch, RootState } from 'store/types';
import {
  dfdDefaultTheme,
  MuiBox,
  MuiGrid,
  MuiIconButton,
  MuiPaper,
  MuiTypography
} from 'theme/material-ui';
import { oc } from 'ts-optchain';
import { NO_CARE_TEAM_FOUND } from 'modules/constants/Provider';
import {
  StyledMuiGrid,
  TagContainer,
  MuiGridAvatarWrapper,
  MuiProviderName,
  StyledMuiButton
} from './styled';

interface MyProviderListItemProps {
  onDelete?: (provider: ProviderSummary) => void;
  provider: ProviderSummary;
}

const MyProviderListItem = ({ onDelete, provider }: MyProviderListItemProps) => {
  const history = useHistory();
  const specialtyName = oc(provider).primarySpecialty.specialtyName();

  const onViewProviderDetail = (provider: ProviderSummary) => {
    const criteria: ProviderDetailCriteria = {
      id: provider.corpProvId,
      idType: ID_TYPE_CORPPROV
    };

    history.push(`/u/provider/${provider.corpProvId}`, { criteria, source: '/u/manage-account' });
  };

  return (
    <MuiBox
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      component={MuiPaper}
      style={{ padding: '24px 8px 24px 48px' }}
      px={1}
      py={3}
      my={2}
    >
      <MuiGrid
        data-testid="preferred-provider-list-item"
        container
        spacing={3}
        alignItems="flex-start"
      >
        <MuiGridAvatarWrapper item xs={2} lg={1}>
          {provider.providerProfileImageUrl ? (
            <LazyMuiAvatar src={provider.providerProfileImageUrl} />
          ) : (
            <Avatar size="medium" />
          )}
        </MuiGridAvatarWrapper>

        <MuiGrid item xs={7} lg={8}>
          <MuiProviderName fontWeight={FontWeight.semibold}>{provider.displayName}</MuiProviderName>

          {specialtyName ? (
            <MuiTypography fontWeight={FontWeight.light} color={Color.grayHue2}>
              {specialtyName}
            </MuiTypography>
          ) : null}
        </MuiGrid>

        {!onDelete ? (
          <StyledMuiGrid item xs={3} lg={3}>
            <MuiIconButton
              aria-label="provider details"
              onClick={() => onViewProviderDetail(provider)}
              style={{ padding: '8px' }}
            >
              <Svg
                name="keyboard_arrow_right"
                set="material"
                size={dfdDefaultTheme.breakpoints.up('sm') ? IconSize.base : IconSize.small}
              />
            </MuiIconButton>
          </StyledMuiGrid>
        ) : null}

        {onDelete ? (
          <StyledMuiGrid item xs={3} lg={3}>
            <MuiIconButton
              aria-label="remove provider"
              onClick={() => onDelete(provider)}
              style={{ padding: '8px' }}
            >
              <Svg
                name="delete"
                set="material"
                size={dfdDefaultTheme.breakpoints.up('sm') ? IconSize.base : IconSize.small}
              />
            </MuiIconButton>
            <MuiIconButton
              aria-label="provider details"
              onClick={() => onViewProviderDetail(provider)}
              style={{ padding: '8px' }}
            >
              <Svg
                name="keyboard_arrow_right"
                set="material"
                size={dfdDefaultTheme.breakpoints.up('sm') ? IconSize.base : IconSize.small}
              />
            </MuiIconButton>
          </StyledMuiGrid>
        ) : null}
      </MuiGrid>
    </MuiBox>
  );
};

interface MyProvidersProps {
  pcpProviders: ProviderSummary[];
  preferredProviders: ProviderSummary[];
  consumerId: string;
  dispatch: RootDispatch;
}

export const MyProviders = ({
  pcpProviders = [],
  preferredProviders = [],
  consumerId,
  dispatch
}: MyProvidersProps) => {
  const [loading, setLoading] = useState(false);

  const allProviders = [...pcpProviders, ...preferredProviders];

  const handleDelete = async (provider: ProviderSummary) => {
    setLoading(true);

    const result = await dispatch(
      updatePreferredDoctorsAndRefreshMyDoctors(consumerId, provider.corpProvId, false)
    );

    if (result.error) {
      Alert.alert(
        'A problem occurred',
        'There was an issue removing this provider. Please try again.'
      );
    }

    setLoading(false);
  };

  return (
    <MuiBox>
      <MuiBox display="flex" justifyContent="flex-end">
        <StyledMuiButton variant="contained" color="primary" component={Link} to="/u/find-a-doctor">
          Add provider
        </StyledMuiButton>
      </MuiBox>
      <MuiBox>
        {loading ? <AbsoluteSpinner /> : null}
        <DataLoader
          data={allProviders}
          renderNoData={() => (
            <MuiBox p={2}>
              <MuiTypography align="center">{NO_CARE_TEAM_FOUND}</MuiTypography>
            </MuiBox>
          )}
          renderData={() => (
            <>
              {pcpProviders.map(provider => (
                <TagContainer>
                  <span>PRIMARY</span>
                  <MyProviderListItem provider={provider} key={provider.npi} />
                </TagContainer>
              ))}

              {preferredProviders.map(provider => (
                <TagContainer>
                  <span>PREFERRED</span>
                  <MyProviderListItem
                    provider={provider}
                    onDelete={handleDelete}
                    key={provider.npi}
                  />
                </TagContainer>
              ))}
            </>
          )}
        />
      </MuiBox>
    </MuiBox>
  );
};

const mapState = (state: RootState) => ({
  pcpProviders: currentConsumerPcpDoctorsSelector(state),
  preferredProviders: currentConsumerPreferredDoctorsSelector(state),
  consumerId: currentAccountConsumerIdSelector(state)
});

export default connect(mapState)(MyProviders);
